import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import ConfirmModal from './ConfirmModal';

function AltTextInput({ itemId, fieldSlug, onUpdate }: { itemId: string, fieldSlug: string, onUpdate: (itemId: string, fieldSlug: string, text: string) => Promise<boolean> }) {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<'error' | null>(null);

  return (
    <div className="flex items-center gap-2 mt-2">
      <input 
        type="text" 
        value={text} 
        onChange={e => setText(e.target.value)} 
        placeholder="Enter alt text..."
        disabled={loading}
        className="bg-[var(--background1)] border border-[var(--border1)] text-[var(--text1)] text-[10px] md:text-[11px] px-2 py-1 rounded-[2px] focus:outline-none focus:border-[var(--blueBorder)]"
      />
      <button 
        onClick={async () => { 
          if(text) { 
            setLoading(true);
            setStatus(null);
            const success = await onUpdate(itemId, fieldSlug, text);
            setLoading(false);
            if (!success) setStatus('error');
          }
        }}
        className="bg-[var(--actionPrimaryBackground)] hover:bg-[var(--actionPrimaryBackgroundHover)] text-[var(--actionPrimaryText)] text-[10px] md:text-[11px] font-medium px-2 py-1 rounded-[2px] transition disabled:opacity-50"
        disabled={!text || loading}
      >
        {loading ? 'Updating...' : 'Update'}
      </button>
      {status === 'error' && <span className="text-[var(--redBackground)] text-[10px] md:text-[11px]">Try again</span>}
    </div>
  );
}

function ImageUploadFix({ onUpload, isFailed }: { onUpload: (file: File, altText?: string) => Promise<boolean>, isFailed: boolean }) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<'error' | null>(isFailed ? 'error' : null);

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-2">
      <label className={`cursor-pointer inline-block ${loading ? 'bg-[var(--background5)] cursor-not-allowed' : 'bg-[var(--actionPrimaryBackground)] hover:bg-[var(--actionPrimaryBackgroundHover)]'} border border-transparent text-[var(--actionPrimaryText)] text-[10px] md:text-[11px] font-medium px-2 py-1 rounded-[2px] transition`}>
        {loading ? 'Uploading...' : 'Replace Image'}
        <input 
          type="file" 
          className="hidden" 
          accept="image/*"
          disabled={loading}
          onChange={async (e) => {
            if (e.target.files && e.target.files[0]) {
              setLoading(true);
              setStatus(null);
              const success = await onUpload(e.target.files[0]);
              setLoading(false);
              if (!success) setStatus('error');
            }
          }} 
        />
      </label>
      {status === 'error' && <span className="text-[var(--redBackground)] text-[10px] md:text-[11px]">Try again</span>}
    </div>
  );
}
function AutoFixButton({ onFix, label = 'Auto-Fix', isFailed }: { onFix: () => Promise<boolean>, label?: string, isFailed: boolean }) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<'error' | null>(isFailed ? 'error' : null);

  return (
    <div className="mt-2 flex items-center gap-2">
      <button aria-label={label}
        onClick={async () => {
          setLoading(true);
          setStatus(null);
          const success = await onFix();
          setLoading(false);
          if (!success) setStatus('error');
        }}
        disabled={loading}
        className="bg-[var(--actionPrimaryBackground)] hover:bg-[var(--actionPrimaryBackgroundHover)] text-[var(--actionPrimaryText)] text-[10px] md:text-[11px] font-medium px-2 py-1 rounded-[2px] transition disabled:opacity-50"
      >
        {loading ? 'Fixing...' : label}
      </button>
      {status === 'error' && <span className="text-[var(--redBackground)] text-[10px] md:text-[11px]">Try again</span>}
    </div>
  );
}

interface GlobalAuditHistoryProps {
  token: string;
  siteId: string | null;
  onAuthError?: () => void;
}

export default function GlobalAuditHistory({ token, siteId, onAuthError }: GlobalAuditHistoryProps) {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [totals, setTotals] = useState<Record<string, number>>({});
  const [filterCollection, setFilterCollection] = useState<string>('');
  
  const [fixedItems, setFixedItems] = useState<Set<string>>(new Set());
  const [failedItems, setFailedItems] = useState<Set<string>>(new Set());
  const [confirmAction, setConfirmAction] = useState<{
    isOpen: boolean;
    title?: string;
    message?: string;
    onConfirm?: () => void;
    onCancel?: () => void;
  }>({ isOpen: false });

  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const fetchLogs = async (isLoadMore = false, isRefresh = false) => {
    if (isLoadMore) setIsLoadingMore(true);
    else if (!isRefresh) setLoading(true);

    try {
      let currentLimit = 20;
      let currentOffset = offset;
      
      if (isRefresh) {
        // Fetch all currently loaded logs to avoid jumping
        currentLimit = offset + 20;
        currentOffset = 0;
      } else if (isLoadMore) {
        currentOffset = offset + 20;
      } else {
        currentLimit = 20;
        currentOffset = 0;
      }

      let url = `${API_BASE_URL}/api/config/all-logs?siteId=${siteId || ''}&limit=${currentLimit}&offset=${currentOffset}`;
      if (filterCollection) {
        url += `&filterCollectionName=${encodeURIComponent(filterCollection)}`;
      }

      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.status === 401 && onAuthError) {
        onAuthError();
        return;
      }
      const data = await res.json();
      const logsData = data.logs || [];
      
      if (data.collectionTotals) {
        setTotals(data.collectionTotals);
        // Automatically select the first collection if none is selected
        if (!filterCollection && Object.keys(data.collectionTotals).length > 0) {
          const firstCollection = Object.keys(data.collectionTotals)[0];
          setFilterCollection(firstCollection);
          // Don't append logs yet, the useEffect will trigger a re-fetch with the new filter
          return;
        }
      }
      
      let mergedLogs = logsData;
      if (isLoadMore) {
        // Append to existing logs before deduplicating
        mergedLogs = [...logs, ...logsData];
      } else if (isRefresh) {
        // Keep logs that are marked as fixed so they don't vanish immediately
        const currentFixedLogs = logs.filter((l: any) => {
           const key = `${l.itemId}-${l.fieldSlug}-${l.ruleType}`;
           return fixedItems.has(key);
        });
        mergedLogs = [...mergedLogs, ...currentFixedLogs];
      }
      
      // Deduplicate logs just in case there are duplicated rows in the DB
      const uniqueLogsMap = new Map();
      mergedLogs.forEach((l: any) => {
        const key = `${l.itemId}-${l.fieldSlug}-${l.ruleType}-${l.error}`;
        if (!uniqueLogsMap.has(key)) {
          uniqueLogsMap.set(key, l);
        }
      });
      const deduplicatedLogs = Array.from(uniqueLogsMap.values());
      
      setLogs(deduplicatedLogs);
      setHasMore(data.hasMore);
      if (!isRefresh && !isLoadMore) setOffset(0);
      else if (isLoadMore) setOffset(currentOffset);
      
      // If this is the initial load, expand the first collection group
      if (deduplicatedLogs.length > 0 && Object.keys(expandedGroups).length === 0 && !isLoadMore && !isRefresh) {
        const firstColName = deduplicatedLogs[0].collectionName || 'Unknown Collection';
        setExpandedGroups({ [firstColName]: true });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchLogs(false, true);
    setIsRefreshing(false);
  };

  useEffect(() => {
    fetchLogs();
    
    const interval = setInterval(() => fetchLogs(false, true), 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, [siteId, filterCollection]);

  const handleUpload = async (file: File, collectionId: string, itemId: string, fieldSlug: string, ruleType: string, altText?: string) => {
    if (!token || !siteId) return false;
    const itemKey = `${itemId}-${fieldSlug}-${ruleType}`;

    const formData = new FormData();
    formData.append('image', file);
    formData.append('collectionId', collectionId);
    formData.append('itemId', itemId);
    formData.append('fieldSlug', fieldSlug);
    formData.append('siteId', siteId);
    if (altText) {
      formData.append('altText', altText);
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/fix/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (res.ok) {
        setFixedItems(prev => new Set(prev).add(itemKey));
        setFailedItems(prev => { const s = new Set(prev); s.delete(itemKey); return s; });
        return true;
      } else {
        const errorData = await res.json().catch(() => ({}));
        if (errorData.error) {
           alert(errorData.error);
        }
        setFailedItems(prev => new Set(prev).add(itemKey));
        return false;
      }
    } catch (err) {
      console.error(err);
      setFailedItems(prev => new Set(prev).add(itemKey));
      return false;
    }
  };

  const handleUpdateAltText = async (collectionId: string, itemId: string, fieldSlug: string, text: string, ruleType: string) => {
    if (!token || !siteId) return false;
    const itemKey = `${itemId}-${fieldSlug}-${ruleType}`;

    try {
      const res = await fetch(`${API_BASE_URL}/api/fix/alt-text`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ collectionId, itemId, fieldSlug, altText: text })
      });
      if (res.ok) {
        setFixedItems(prev => new Set(prev).add(itemKey));
        setFailedItems(prev => { const s = new Set(prev); s.delete(itemKey); return s; });
        return true;
      } else {
        setFailedItems(prev => new Set(prev).add(itemKey));
        return false;
      }
    } catch (err) {
      console.error(err);
      setFailedItems(prev => new Set(prev).add(itemKey));
      return false;
    }
  };

  const handleAutoFixText = async (collectionId: string, itemId: string, fieldSlug: string, ruleType: string) => {
    if (!token || !siteId) return false;
    const itemKey = `${itemId}-${fieldSlug}-${ruleType}`;

    try {
      const res = await fetch(`${API_BASE_URL}/api/fix/text`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ collectionId, itemId, fieldSlug, ruleType })
      });
      if (res.ok) {
        setFixedItems(prev => new Set(prev).add(itemKey));
        setFailedItems(prev => { const s = new Set(prev); s.delete(itemKey); return s; });
        return true;
      } else {
        setFailedItems(prev => new Set(prev).add(itemKey));
        return false;
      }
    } catch (err) {
      setFailedItems(prev => new Set(prev).add(itemKey));
      return false;
    }
  };

  const handleAutoFixBrokenLink = async (collectionId: string, itemId: string, fieldSlug: string) => {
    if (!token || !siteId) return false;
    const itemKey = `${itemId}-${fieldSlug}-BROKEN_LINKS`;

    try {
      const res = await fetch(`${API_BASE_URL}/api/fix/broken-link`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ collectionId, itemId, fieldSlug })
      });
      if (res.ok) {
        setFixedItems(prev => new Set(prev).add(itemKey));
        setFailedItems(prev => { const s = new Set(prev); s.delete(itemKey); return s; });
        return true;
      } else {
        setFailedItems(prev => new Set(prev).add(itemKey));
        return false;
      }
    } catch (err) {
      setFailedItems(prev => new Set(prev).add(itemKey));
      return false;
    }
  };

  if (loading && logs.length === 0) {
    return <div className="text-[12px] md:text-[13px] text-[var(--text3)] text-center">Loading project-wide background activity...</div>;
  }

  if (logs.length === 0) {
    return (
      <div className="flex flex-col h-full w-full p-1">
        <div className="flex justify-between items-center mb-2 pb-2 border-b border-[#2a2a2a]">
          <h3 className="text-[14px] md:text-[15px] font-medium text-[var(--text1)]">Project-wide Background Activity</h3>
        </div>
        <p className="text-[12px] md:text-[13px] text-[var(--text3)] mb-4 px-1">
          Issues resolved from the Audited Results pane will automatically be removed from this list when the background sync job runs on its scheduled time.
        </p>
        <div className="text-[12px] md:text-[13px] text-[var(--text3)] text-center py-6">
          Set new activity by exploring collections and schedule.
        </div>
      </div>
    );
  }

  // Group logs by collectionName
  const groupedLogs = logs.reduce((acc, log) => {
    const colName = log.collectionName || 'Unknown Collection';
    if (!acc[colName]) {
      acc[colName] = [];
    }
    acc[colName].push(log);
    return acc;
  }, {} as Record<string, any[]>);

  return (
    <div className="flex flex-col h-full w-full p-1">
      <div className="flex justify-between items-center mb-2 pb-2 border-b border-[#2a2a2a]">
        <h3 className="text-[14px] md:text-[15px] font-medium text-[var(--text1)]">Project-wide Background Activity</h3>
        <div className="flex items-center gap-3">
          {Object.keys(totals).length > 0 && (
            <select
              value={filterCollection}
              onChange={(e) => {
                setFilterCollection(e.target.value);
                setLogs([]); // Clear current logs while fetching new ones
              }}
              className="text-[11px] md:text-[12px] bg-[var(--background2)] border border-[#2a2a2a] text-[var(--text1)] rounded px-2 py-1 outline-none focus:border-[var(--blueBorder)] transition-colors"
            >
              {Object.keys(totals).map(col => (
                <option key={col} value={col}>
                  {col} ({totals[col]})
                </option>
              ))}
            </select>
          )}
          <button onClick={handleRefresh} disabled={isRefreshing} className="text-[11px] md:text-[12px] text-[var(--blueText)] hover:text-[#1456b3] disabled:opacity-50 flex items-center gap-1">
          {isRefreshing ? (
            <>
              <svg className="animate-spin w-3 h-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Refreshing...
            </>
          ) : 'Refresh'}
          </button>
        </div>
      </div>
      <p className="text-[12px] md:text-[13px] text-[var(--text3)] mb-4 px-1">
        Issues resolved from the Audited Results pane will automatically be removed from this list when the background sync job runs on its scheduled time.
      </p>
      
      <div className="flex-1 overflow-y-auto pr-2 space-y-4">
        {Object.entries(groupedLogs).map(([collectionName, collectionLogs]: [string, any]) => {
          const isExpanded = expandedGroups[collectionName];
          return (
            <div key={collectionName} className="border border-[var(--border1)] rounded-[4px] overflow-hidden">
              <button 
                aria-expanded={isExpanded}
                className="w-full flex justify-between items-center bg-[var(--background2)] hover:bg-[var(--background3)] p-3 text-left transition"
                onClick={() => setExpandedGroups(prev => ({ ...prev, [collectionName]: !prev[collectionName] }))}
              >
                <div className="flex items-center gap-2">
                  <span className="text-[13px] md:text-[14px] font-semibold text-[var(--text1)]">{collectionName}</span>
                  <span className="text-[11px] md:text-[12px] bg-[var(--redBackground)] text-white px-1.5 py-0.5 rounded-sm border border-[var(--redBorder)]">
                    {collectionLogs.length} issues found {totals[collectionName] ? `out of ${totals[collectionName]} total` : ''}
                  </span>
                </div>
                <svg 
                  className={`w-4 h-4 text-[var(--text3)] transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isExpanded && (
                <div className="p-3 bg-[var(--background1)] space-y-2 border-t border-[var(--border1)]">
                  {collectionLogs.map((log: any) => {
                    const itemKey = `${log.itemId}-${log.fieldSlug}-${log.ruleType}`;
                    const isFixed = fixedItems.has(itemKey);
                    const isFailed = failedItems.has(itemKey);

                    return (
                      <div key={itemKey} className={`p-3 border rounded-[3px] ${isFixed ? 'bg-[var(--greenBackground)]/10 border-[var(--greenBorder)]/30' : isFailed ? 'bg-[var(--redBackground)]/10 border-[var(--redBorder)]/30' : 'bg-[var(--background2)] border-[var(--border1)]'}`}>
                        <div className="flex justify-between items-start mb-1">
                          <span className={`font-medium text-[12px] md:text-[13px] ${isFixed ? 'text-[var(--greenBackgroundHover)]' : 'text-[var(--text1)]'}`}>{log.itemName}</span>
                          <span className="text-[10px] md:text-[11px] text-[var(--textInactive)]">{new Date(log.createdAt).toLocaleString()}</span>
                        </div>
                        <p className={`text-[11px] md:text-[12px] ${isFixed ? 'text-[var(--greenBackgroundHover)]' : 'text-[var(--redBackground)] font-medium'}`}>
                          {isFixed ? 'Successfully updated Webflow CMS item!' : log.error}
                        </p>

                        {!isFixed && (log.ruleType === 'MAX_IMAGE_SIZE' || log.ruleType === 'ALLOWED_IMAGE_FORMATS') && log.fieldSlug && (
                          <ImageUploadFix 
                            isFailed={isFailed}
                            onUpload={async (file, altText) => {
                              return await handleUpload(file, log.collectionConfig?.collectionId || log.collectionId, log.itemId, log.fieldSlug, log.ruleType, altText);
                            }}
                          />
                        )}
                        {!isFixed && log.ruleType === 'MISSING_ALT_TEXT' && log.fieldSlug && (
                          <AltTextInput 
                            itemId={log.itemId} 
                            fieldSlug={log.fieldSlug} 
                            onUpdate={(iid, fs, text) => handleUpdateAltText(log.collectionConfig?.collectionId || log.collectionId, iid, fs, text, log.ruleType)} 
                          />
                        )}
                        {!isFixed && (log.ruleType === 'TRAILING_SPACES' || log.ruleType === 'TEXT_CASING') && log.fieldSlug && (
                          <AutoFixButton 
                            isFailed={isFailed}
                            label="Auto-Fix Text"
                            onFix={() => handleAutoFixText(log.collectionConfig?.collectionId || log.collectionId, log.itemId, log.fieldSlug, log.ruleType)}
                          />
                        )}
                        {!isFixed && log.ruleType === 'BROKEN_LINKS' && log.fieldSlug && (
                          <AutoFixButton 
                            isFailed={isFailed}
                            label="Remove Broken Links"
                            onFix={() => {
                              return new Promise<boolean>((resolve) => {
                                setConfirmAction({
                                  isOpen: true,
                                  title: 'Remove Broken Link',
                                  message: 'Are you sure you want to remove this broken link? This will modify the CMS item directly.',
                                  onConfirm: async () => {
                                    setConfirmAction({ isOpen: false });
                                    const success = await handleAutoFixBrokenLink(log.collectionConfig?.collectionId || log.collectionId, log.itemId, log.fieldSlug);
                                    resolve(success);
                                  },
                                  onCancel: () => {
                                    setConfirmAction({ isOpen: false });
                                    resolve(true);
                                  }
                                });
                              });
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {hasMore && (
          <div className="py-4 text-center">
            <button
              onClick={() => fetchLogs(true)}
              disabled={isLoadingMore}
              className="bg-[var(--background3)] hover:bg-[var(--background4)] border border-[#3a3a3a] text-[var(--text1)] text-[12px] md:text-[13px] font-medium py-1.5 px-4 rounded transition duration-150 disabled:opacity-50"
            >
              {isLoadingMore ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmAction.isOpen}
        title={confirmAction.title || ''}
        message={confirmAction.message || ''}
        onConfirm={confirmAction.onConfirm || (() => {})}
        onCancel={confirmAction.onCancel || (() => {})}
        confirmText="Remove Link"
      />
    </div>
  );
}
